import BybMathExpressions from "@Components/game/common/byb-math-expressions";
import BybVrDialog from "@Components/game/common/dialog/byb-vr-dialog";
import useI18n from "@i18n";
import { Stack, Typography, useTheme } from "@mui/material";
import DecisionMetricKeys from "@Shared/enums/DecisionMetricKeys";
import { hexToRgba } from "@Shared/utilities/hexToRgba";

interface IDecisionScoreInfoDialog {
  open: boolean;
  onClose: () => void;
}

const DECISION_METRIC_KEYS = Object.values(DecisionMetricKeys);

const DecisionScoreInfoDialog = ({ open, onClose }: IDecisionScoreInfoDialog) => {
  const theme = useTheme();
  const { i18n } = useI18n();

  return (
    <BybVrDialog open={open} onClose={onClose} name="scores-info" showCloseButton width="70vw">
      <Stack spacing={4} padding={4}>
        <Stack spacing={2} textAlign="center">
          <Typography variant="text3xl700" color="text.primary">
            {i18n("session-results.scores-dialog.title")}
          </Typography>
          <Typography variant="textBase400" color="text.secondary">
            {i18n("session-results.scores-dialog.subtitle")}
          </Typography>
        </Stack>

        <Stack
          spacing={0}
          sx={{
            border: `1px solid ${theme.palette.dsBase.v200}10`,
            borderRadius: theme.spacing(3),
            padding: theme.spacing(1, 3),
            backgroundColor: hexToRgba(theme.palette.dsSurface.v300, theme.palette.dsOpacity.low),
          }}
        >
          {DECISION_METRIC_KEYS.map((key, index) => (
            <Stack
              key={key}
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-start"
              gap={2}
              sx={{
                paddingTop: theme.spacing(1.25),
                paddingBottom: theme.spacing(2),
                borderBottom: index < DECISION_METRIC_KEYS.length - 1 ? `1px solid ${theme.palette.dsBase.v200}10` : "none",
              }}
            >
              <Typography variant="textBase400" color="text.secondary" minWidth={275}>
                {i18n(`session-results.scores-dialog.${key}.title`)}
              </Typography>
              <Typography variant="textBase400" color="text.primary">
                {i18n(`session-results.scores-dialog.${key}.description`)}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack spacing={2}>
          <Typography variant="textLg700" color="text.primary">
            {i18n("session-results.scores-dialog.how-it-works.title")}
          </Typography>
          <Typography variant="textBase400" color="text.secondary">
            {i18n("session-results.scores-dialog.how-it-works.description")}
          </Typography>

          <Stack spacing={1} alignItems="center">
            <BybMathExpressions
              formula={i18n("session-results.scores-dialog.how-it-works.formula")}
              displayMode
              sx={{ fontSize: "1.25rem", color: "text.primary", fontWeight: 700 }}
            />
            <BybMathExpressions
              formula={i18n("session-results.scores-dialog.how-it-works.sub-formula")}
              displayMode
              sx={{ fontSize: "0.9rem", color: "text.secondary" }}
            />
          </Stack>

          <Typography variant="textBase400" color="text.secondary">
            {i18n("session-results.scores-dialog.how-it-works.overall-score")}
          </Typography>
        </Stack>
      </Stack>
    </BybVrDialog>
  );
};

export default DecisionScoreInfoDialog;
